import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountProfile, AccountStat } from './accounts-information.models';
import { AccountStatCardComponent } from './account-stat-card';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-account-profile-card',
  standalone: true,
  imports: [CommonModule, AccountStatCardComponent, TranslatePipe],
  template: `
    <div
      class="bg-surface rounded-2xl sm:rounded-3xl border border-border-subtle shadow-subtle overflow-hidden antialiased relative"
    >
      <div
        class="h-28 sm:h-32 md:h-36 bg-gradient-to-r from-bg-base via-surface to-bg-base relative overflow-hidden"
      >
        <div
          class="absolute inset-0 bg-primary-soft opacity-20"
        ></div>
        <div
          class="absolute bottom-0 inset-x-0 h-16 sm:h-20 bg-gradient-to-t from-surface via-surface/40 to-transparent z-10"
        ></div>
      </div>

      <div class="px-4 sm:px-6 md:px-8 pb-5 sm:pb-6 md:pb-8 relative z-20">
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-start gap-4 sm:gap-6 -mt-14 sm:-mt-16 mb-5 sm:mb-6 transition-transform"
          [class.cursor-pointer]="clickableHeader"
          [class.hover:scale-[1.01]]="clickableHeader"
          (click)="onHeaderClick()"
        >
          <div class="relative group shrink-0">
            <div
              class="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden border-4 border-border shadow-subtle bg-surface-hover relative z-10 transition-transform duration-300 group-hover:scale-[1.02]"
            >
              <img
                [src]="profile.avatarUrl || 'assets/default-avatar.png'"
                [alt]="profile.name.startsWith('accountsInformation.') ? (profile.name | translate) : profile.name"
                class="w-full h-full object-cover"
              />
            </div>
          </div>

          <div class="flex-1 sm:pt-14 md:pt-16 w-full min-w-0">
            <div class="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-3 sm:gap-4">
              <div class="space-y-1 min-w-0 w-full sm:w-auto">
                <h1
                  class="text-base sm:text-lg md:text-xl font-black text-text-primary tracking-tight flex items-center justify-center sm:justify-start gap-1.5 flex-wrap"
                >
                  <span class="break-words">{{ profile.name.startsWith('accountsInformation.') ? (profile.name | translate) : profile.name }}</span>
                  <span class="inline-flex text-primary shrink-0" [title]="'accountsInformation.profile.verifiedTitle' | translate">
                    <i class="fa-solid fa-circle-check text-xs sm:text-sm"></i>
                  </span>
                </h1>

                <p
                  class="text-xs sm:text-sm font-bold text-primary uppercase tracking-wider font-mono break-words"
                >
                  {{ profile.role.startsWith('accountsInformation.') ? (profile.role | translate) : profile.role }}
                </p>

                @if (profile.location || profile.memberSince) {
                  <div
                    class="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 sm:gap-x-4 gap-y-1.5 text-[11px] sm:text-xs text-text-muted font-medium pt-0.5"
                  >
                    @if (profile.location) {
                      <span class="inline-flex items-center gap-1 break-words">
                        <i class="fa-solid fa-location-dot text-text-muted shrink-0"></i>
                        <span>{{ profile.location.startsWith('accountsInformation.') ? (profile.location | translate) : profile.location }}</span>
                      </span>
                    }
                    @if (profile.memberSince) {
                      <span
                        class="inline-flex items-center gap-1 font-mono text-[10px] sm:text-[11px] uppercase tracking-wider shrink-0"
                      >
                        <i class="fa-solid fa-calendar-days text-text-muted shrink-0"></i>
                        <span>{{ 'accountsInformation.profile.joined' | translate }} {{ profile.memberSince.startsWith('accountsInformation.') ? (profile.memberSince | translate) : profile.memberSince }}</span>
                      </span>
                    }
                  </div>
                }
              </div>

              <button
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-surface/90 sm:bg-surface-elevated hover:bg-surface-hover border border-border-subtle text-text-muted hover:text-text-primary flex items-center justify-center transition-all duration-200 cursor-pointer active:scale-95 shadow-sm shrink-0 absolute top-3 end-3 sm:relative sm:top-auto sm:end-auto z-30 backdrop-blur-sm"
                type="button"
                (click)="$event.stopPropagation()"
                [title]="'accountsInformation.actions.shareProfile' | translate"
              >
                <i class="fa-solid fa-share-nodes text-xs"></i>
              </button>
            </div>

            <div
              class="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-2.5 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-dashed border-border-subtle"
            >
              <div class="flex items-center gap-0.5 shrink-0">
                @for (star of ratingStars; track star) {
                  <i
                    class="fa-solid fa-star text-[11px] sm:text-xs transition-colors duration-200"
                    [class.text-primary]="star <= roundedRating"
                    [class.text-border-strong]="star > roundedRating"
                  ></i>
                }
              </div>
              <div class="flex items-baseline gap-1 font-mono shrink-0">
                <span class="text-sm sm:text-base font-black text-text-primary tracking-tight">{{
                  profile.rating
                }}</span>
                <span class="text-[10px] sm:text-[11px] font-medium text-text-muted">/5.0</span>
              </div>
              <span
                class="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-text-muted font-mono"
              >
                ({{ profile.reviewsCount }} {{ 'accountsInformation.profile.verifiedRecords' | translate }})
              </span>
            </div>
          </div>
        </div>

        @if (profile.bio) {
          <div class="mb-5 sm:mb-6 bg-surface-elevated border border-border-subtle p-3.5 sm:p-4 rounded-2xl">
            <h4
              class="text-[10px] font-black uppercase tracking-widest text-text-muted font-mono mb-1 sm:mb-1.5 text-center sm:text-start"
            >
              {{ 'accountsInformation.profile.editorialBio' | translate }}
            </h4>
            <p class="text-xs sm:text-[13px] font-medium text-text-secondary leading-relaxed text-start">
              {{ profile.bio.startsWith('accountsInformation.') ? (profile.bio | translate) : profile.bio }}
            </p>
          </div>
        }

        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3.5 md:gap-4">
          @for (stat of stats; track stat.label) {
            <app-account-stat-card [stat]="stat"></app-account-stat-card>
          }
        </div>
      </div>
    </div>
  `,
})
export class AccountProfileCardComponent {
  @Input({ required: true }) profile!: AccountProfile;
  @Input() stats: AccountStat[] = [];
  @Input() clickableHeader = false;
  @Output() headerClick = new EventEmitter<void>();

  readonly ratingStars = [1, 2, 3, 4, 5];

  get roundedRating(): number {
    return Math.round(this.profile?.rating ?? 0);
  }

  onHeaderClick(): void {
    if (this.clickableHeader) {
      this.headerClick.emit();
    }
  }
}
